import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./HomeAboutSection.css";

export default function HomeAboutSection() {
  const sectionRef = useRef(null);
  const spacerRef = useRef(null);
  const leftImgRef = useRef(null);
  const rightImgRef = useRef(null);

  const scrollLocked = useRef(false);
  const sectionFullyInView = useRef(false);
  const fakeScroll = useRef(0);
  const lastTouchY = useRef(0);
  const animationActive = useRef(false);

  const [isInView, setIsInView] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const section = sectionRef.current;
    const left = leftImgRef.current;
    const right = rightImgRef.current;

    if (!section || !left || !right) return;

    const textObserver = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.3 }
    );
    textObserver.observe(section);

    function checkSectionInView() {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      const fullyVisible = rect.top >= -60 && rect.bottom <= vh + 60;
      sectionFullyInView.current = fullyVisible;
      return fullyVisible;
    }

    function easeInOut(t) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    function bellCurve(t) {
      return Math.sin(Math.PI * t);
    }

    function applyAnimation(p) {
      const vH = window.innerHeight;
      const imgH = left.offsetHeight;

      const start  =  vH + 100;
      const center = (vH - imgH) / 2;
      const end    = -(imgH + 100);

      // ─── LEFT IMAGE — p: 0 → 1 ───────────────────────────────
      const pL = Math.max(0, Math.min(1, p));

      if (pL > 0 && pL < 1) {
        const rotateL = -15 * bellCurve(pL);
        let yL;
        if (pL < 0.5) {
          const t = easeInOut(pL * 2);
          yL = start + (center - start) * t;
        } else {
          const t = easeInOut((pL - 0.5) * 2);
          yL = center + (end - center) * t;
        }
        left.style.opacity    = "1";
        left.style.visibility = "visible";
        left.style.transform  = `translateY(${yL}px) rotate(${rotateL}deg)`;
      } else {
        left.style.opacity    = "0";
        left.style.visibility = "hidden";
        left.style.transform  = `translateY(${pL <= 0 ? start : end}px) rotate(0deg)`;
      }

      // ─── RIGHT IMAGE — p: 1 → 2 ──────────────────────────────
      const pR = Math.max(0, Math.min(1, p - 1));

      if (pR > 0 && pR < 1) {
        const rotateR = 15 * bellCurve(pR);
        let yR;
        if (pR < 0.5) {
          const t = easeInOut(pR * 2);
          yR = start + (center - start) * t;
        } else {
          const t = easeInOut((pR - 0.5) * 2);
          yR = center + (end - center) * t;
        }
        right.style.opacity    = "1";
        right.style.visibility = "visible";
        right.style.transform  = `translateY(${yR}px) rotate(${rotateR}deg)`;
      } else {
        right.style.opacity    = "0";
        right.style.visibility = "hidden";
        right.style.transform  = `translateY(${pR <= 0 ? start : end}px) rotate(0deg)`;
      }

      // Track if animation is mid-way (not at 0 or 2)
      animationActive.current = p > 0 && p < 2;
    }

    function lockScroll() {
      if (scrollLocked.current) return;
      scrollLocked.current = true;
      document.body.style.overflow = "hidden";
    }

    function unlockScroll() {
      if (!scrollLocked.current) return;
      scrollLocked.current = false;
      document.body.style.overflow = "";
    }

    const SCROLL_SENSITIVITY = 0.0008;
    const TOUCH_SENSITIVITY  = 0.003;

    function handleWheel(e) {
      if (!checkSectionInView()) {
        // If animation is mid-way and user scrolled out, don't reset
        if (scrollLocked.current) unlockScroll();
        return;
      }

      const dir = e.deltaY > 0 ? 1 : -1;

      // Scrolling down and already at end → let page scroll
      if (dir > 0 && fakeScroll.current >= 2) {
        unlockScroll();
        return;
      }
      // Scrolling up and already at start → let page scroll
      if (dir < 0 && fakeScroll.current <= 0) {
        unlockScroll();
        return;
      }

      // Animation range: lock scroll and animate
      e.preventDefault();
      lockScroll();

      const step = Math.abs(e.deltaY) * SCROLL_SENSITIVITY;
      fakeScroll.current = Math.max(0, Math.min(2, fakeScroll.current + dir * step));
      applyAnimation(fakeScroll.current);

      // Reached boundary → unlock
      if (
        (dir > 0 && fakeScroll.current >= 2) ||
        (dir < 0 && fakeScroll.current <= 0)
      ) {
        unlockScroll();
      }
    }

    function onTouchStart(e) {
      lastTouchY.current = e.touches[0].clientY;
    }

    function onTouchMove(e) {
      const currentY = e.touches[0].clientY;
      const dy = lastTouchY.current - currentY;
      lastTouchY.current = currentY;

      if (Math.abs(dy) < 2) return;
      if (!checkSectionInView()) return;

      const dir = dy > 0 ? 1 : -1;

      if (dir > 0 && fakeScroll.current >= 2) {
        unlockScroll();
        return;
      }
      if (dir < 0 && fakeScroll.current <= 0) {
        unlockScroll();
        return;
      }

      e.preventDefault();
      lockScroll();

      const step = Math.abs(dy) * TOUCH_SENSITIVITY;
      fakeScroll.current = Math.max(0, Math.min(2, fakeScroll.current + dir * step));
      applyAnimation(fakeScroll.current);

      if (
        (dir > 0 && fakeScroll.current >= 2) ||
        (dir < 0 && fakeScroll.current <= 0)
      ) {
        unlockScroll();
      }
    }

    function onTouchEnd() {
      if (fakeScroll.current >= 2 || fakeScroll.current <= 0) {
        unlockScroll();
      }
    }

    function onScroll() {
      const inView = checkSectionInView();

      // Only reset fakeScroll if section is NOT in view AND animation is not active
      // This prevents resetting when user scrolls back up toward the section
      if (!inView && !scrollLocked.current && !animationActive.current) {
        // Section scrolled far below viewport (user hasn't reached it yet) → reset
        const rect = section.getBoundingClientRect();
        const vh = window.innerHeight;
        if (rect.top > vh + 200) {
          // Section is far below, reset so animation plays fresh
          fakeScroll.current = 0;
          applyAnimation(0);
        }
        // If section scrolled far above (user passed it going down), keep fakeScroll at 2
        // so that when user scrolls back up, reverse animation plays correctly
        if (rect.bottom < -200) {
          fakeScroll.current = 2;
          applyAnimation(2);
        }
      }
    }

    window.addEventListener("scroll",     onScroll,     { passive: true  });
    window.addEventListener("wheel",      handleWheel,  { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true  });
    window.addEventListener("touchmove",  onTouchMove,  { passive: false });
    window.addEventListener("touchend",   onTouchEnd,   { passive: true  });

    return () => {
      unlockScroll();
      window.removeEventListener("scroll",     onScroll);
      window.removeEventListener("wheel",      handleWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove",  onTouchMove);
      window.removeEventListener("touchend",   onTouchEnd);
      textObserver.disconnect();
    };
  }, []);

  return (
    <>
      <section ref={sectionRef} className="about-section">
        <div className="about-inner">
          <img
            ref={leftImgRef}
            className="img left"
            src="https://wonder-theme-beauty.myshopify.com/cdn/shop/files/image-paralax-1_1c4bb8f6-2379-477a-844a-c205a34a1108.jpg"
            alt=""
          />

          <img
            ref={rightImgRef}
            className="img right"
            src="https://wonder-theme-beauty.myshopify.com/cdn/shop/files/image-paralax-2_683c405d-ec27-405f-86b9-4bc077d1a1a8.jpg"
            alt=""
          />

          <div className={`content-wrapper ${isInView ? "animate" : ""}`}>
            <h1>Where Every Skincare Moment Counts.</h1>

            <p>
              At Beauty, we believe that skincare isn't just a routine; it's a
              journey of self-care and self-discovery. Our curated selection of
              premium skincare products is designed to elevate your beauty
              routine.
            </p>

            <button onClick={() => navigate("/about")}>ABOUT US</button>

            <div className="down-arrow">
              <span></span>
            </div>
          </div>
        </div>
      </section>

      <div ref={spacerRef} className="about-section-spacer"></div>
    </>
  );
}